import { Howl } from 'howler'
import { useConfigurationStore } from '@stores'
import ultraPlaySoundsUrl from '@assets/ultra-play/ultraPlaySounds.mp3'

// Non-engine (howler) sound effects for Ultra Play, played from a single sprite file. SPRITE below
// is the canonical clip map — transcribed from the design sprite timings (seconds) into ms.
export const ULTRA_PLAY_SOUND = {
    DECREASE_BET: 'ultraPlayButtonDecreaseBet',
    INCREASE_BET: 'ultraPlayButtonIncreaseBet',
    INFO_1: 'ultraPlayButtonInfo1',
    INFO_2: 'ultraPlayButtonInfo2',
    NUMBER_SPINS: 'ultraPlayButtonNumberSpins',
    PLAY: 'ultraPlayButtonPlay',
    COLLAPSE_ROWS: 'ultraPlayCollapseRows',
    END_ROUND: 'ultraPlayEndRound',
    ENTERING_FREE_SPINS: 'ultraPlayEnteringFreeSpins',
    FREE_SPINS_END: 'ultraPlayFreeSpinsEnd',
    RESULT_ROW_APPEAR: 'ultraPlayResultRowAppear',
    WIN_1: 'ultraPlayWin1',
    WIN_2: 'ultraPlayWin2',
    WIN_3: 'ultraPlayWin3',
} as const

export type UltraPlaySoundName = (typeof ULTRA_PLAY_SOUND)[keyof typeof ULTRA_PLAY_SOUND]

// [offsetMs, durationMs] per sprite clip (design timings in seconds × 1000).
const SPRITE: Record<UltraPlaySoundName, [number, number]> = {
    [ULTRA_PLAY_SOUND.DECREASE_BET]: [0, 1378],
    [ULTRA_PLAY_SOUND.INCREASE_BET]: [1428, 1378],
    [ULTRA_PLAY_SOUND.INFO_1]: [2856, 155],
    [ULTRA_PLAY_SOUND.INFO_2]: [3061, 236],
    [ULTRA_PLAY_SOUND.NUMBER_SPINS]: [3347, 170],
    [ULTRA_PLAY_SOUND.PLAY]: [3567, 2000],
    [ULTRA_PLAY_SOUND.COLLAPSE_ROWS]: [5617, 347],
    [ULTRA_PLAY_SOUND.END_ROUND]: [6014, 1867],
    [ULTRA_PLAY_SOUND.ENTERING_FREE_SPINS]: [7930, 1931],
    [ULTRA_PLAY_SOUND.FREE_SPINS_END]: [9911, 2622],
    [ULTRA_PLAY_SOUND.RESULT_ROW_APPEAR]: [12584, 250],
    [ULTRA_PLAY_SOUND.WIN_1]: [12884, 433],
    [ULTRA_PLAY_SOUND.WIN_2]: [13367, 1184],
    [ULTRA_PLAY_SOUND.WIN_3]: [14601, 1548],
}

let howl: Howl | null = null
let initFailed = false

const getHowl = (): Howl | null => {
    if (howl || initFailed) return howl

    try {
        howl = new Howl({ src: [ultraPlaySoundsUrl], sprite: SPRITE })
    } catch {
        // Audio unavailable (e.g. headless/test env) — fail quietly, never block the UI.
        initFailed = true
        howl = null
    }

    return howl
}

// Map a win's multiplier (win / bet) to the small / medium / big win sound.
export const winSoundForMultiplier = (multiplier: number): UltraPlaySoundName =>
    multiplier < 5 ? ULTRA_PLAY_SOUND.WIN_1 : multiplier <= 20 ? ULTRA_PLAY_SOUND.WIN_2 : ULTRA_PLAY_SOUND.WIN_3

// Play an Ultra Play SFX. Respects the platform SFX mute and never throws.
export const playUltraPlaySound = (name: UltraPlaySoundName): void => {
    if (useConfigurationStore.getState().isSoundMuted) return

    try {
        getHowl()?.play(name)
    } catch {
        // ignore playback errors (autoplay policy, disposed context, etc.)
    }
}
